/**
 * ROOF VERTICAL CUTS (2026-10-08, Matt: "In Revit we can put vertical voids into roofs
 * with a special tool … it simply acts as a vertical boolean cut"). Revit's Vertical
 * Opening: closed plan loops stored on the roof (`params.verticalCuts`) that cut straight
 * down through the FINISHED roof. They never take part in the logic that solves the roof
 * — the skeleton, slopes, gables and joins all solve on the drawn bearing line exactly as
 * before; the cut happens afterwards.
 *
 * Why it exists: an eave that runs into a taller part of the building (a lower roof "T"ing
 * into a wall) used to model rafter tails, fascia, gutters, soffits and downspouts inside
 * the wall — wrong for the model and wrong for any quantity taken off it. A cut removes
 * everything the roof derives inside the loop, and the cut edges stay BARE: the body gets a
 * plain cut face, never eave dressing.
 *
 * Two mechanisms, one result:
 *  - the BODY: the solved faces are clipped against the loops (the E5 join's precedent —
 *    `clipFacesToJoined`) before the body is built, so every top/underside/eave crop/hollow
 *    sheet is built from the clipped rings and the cut edge gets its plain band.
 *  - everything DERIVED off the roof (framing + show members, fascia, gutters, flat soffit
 *    corbels, automatic downspouts, painted roofing, tiles): their finished meshes and
 *    feature lines are cut by `clipSolidToCuts` — a true vertical boolean on triangles, so
 *    no generator has to learn about cuts and none can leak a piece through one.
 */
import type { Path2D, Pt2, ResolvedElement } from '@aj/bim-kernel'
import { flattenPathPts } from '@aj/bim-kernel'
import type { ElementSolid } from '../wall/generate3d'
import { clipperReady, differenceClosed, intersectClosed, signedArea } from '../../clipper'
import type { RoofFace } from './solve'

const AREA_EPS = 1e-6
/** vertex weld after clipping — larger than clipper's 1e-4 ft grid, far below anything drawn */
const WELD = 2e-3

/** the roof's cut loops as plan rings, wound counter-clockwise; [] when none */
export function roofCutRings(el: ResolvedElement): Pt2[][] {
  // OVERFRAME (overframe.ts): the regions the roofs this one overframes onto take away, derived
  // live and stamped on by withOverframe — cut exactly like a drawn loop
  const of = el.params.__overframeCuts as unknown
  const derived = Array.isArray(of) ? (of as Pt2[][]).filter((r) => Array.isArray(r) && r.length >= 3) : []
  // CUPOLAS (2026-10-09): the throats of the cupolas sitting on this roof (cupola/layout.ts#withCupolaCuts)
  const cu = el.params.__cupolaCuts as unknown
  if (Array.isArray(cu)) derived.push(...(cu as Pt2[][]).filter((r) => Array.isArray(r) && r.length >= 3))
  const raw = el.params.verticalCuts as Path2D[] | null | undefined
  if (!Array.isArray(raw) || !raw.length) return derived
  const out: Pt2[][] = [...derived]
  for (const p of raw) {
    if (!p || !Array.isArray(p.nodes) || p.nodes.length < 3) continue
    let ring = flattenPathPts({ ...p, closed: true })
    if (ring.length > 1) {
      const a = ring[0], b = ring[ring.length - 1]
      if (Math.hypot(a[0] - b[0], a[1] - b[1]) < 1e-9) ring = ring.slice(0, -1)
    }
    if (ring.length < 3) continue
    const A = signedArea(ring)
    if (Math.abs(A) < AREA_EPS) continue
    out.push(A > 0 ? ring : [...ring].reverse())
  }
  return out
}

export const pointInRing = (pt: Pt2, ring: Pt2[]): boolean => {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j]
    if ((yi > pt[1]) !== (yj > pt[1]) && pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

type Box = [number, number, number, number]
const boxOf = (ring: Pt2[]): Box => {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
  for (const p of ring) {
    if (p[0] < x0) x0 = p[0]; if (p[0] > x1) x1 = p[0]
    if (p[1] < y0) y0 = p[1]; if (p[1] > y1) y1 = p[1]
  }
  return [x0, y0, x1, y1]
}
const boxesMeet = (a: Box, b: Box, pad = 1e-6) =>
  a[0] <= b[2] + pad && b[0] <= a[2] + pad && a[1] <= b[3] + pad && b[1] <= a[3] + pad

/** true when segment P→Q runs along a cut boundary (the bare cut edge) */
export function onCutBoundary(P: Pt2, Q: Pt2, cuts: Pt2[][], tol = 5e-4): boolean {
  const d = (p: Pt2, a: Pt2, b: Pt2) => {
    const abx = b[0] - a[0], aby = b[1] - a[1], l2 = abx * abx + aby * aby
    let t = l2 > 1e-18 ? ((p[0] - a[0]) * abx + (p[1] - a[1]) * aby) / l2 : 0
    t = Math.max(0, Math.min(1, t))
    return Math.hypot(p[0] - (a[0] + abx * t), p[1] - (a[1] + aby * t))
  }
  for (const c of cuts) {
    for (let i = 0; i < c.length; i++) {
      const a = c[i], b = c[(i + 1) % c.length]
      if (d(P, a, b) <= tol && d(Q, a, b) <= tol) return true
    }
  }
  return false
}

/**
 * Face ring minus the cuts, as SIMPLE rings (no holes — the body builder earcuts each
 * face ring on its own). A cut lying wholly inside a face would leave a hole, so the face
 * is first split by a vertical line through that hole (a connected hole spanning
 * [minX, maxX] always crosses its own mid-x), each half cut separately; the split's two
 * sides are coplanar, so the body draws no band or line along it.
 */
export function cutRingSimple(ring: Pt2[], cuts: Pt2[][], depth = 0): Pt2[][] {
  const res = differenceClosed([ring], cuts).filter((r) => r.length >= 3 && Math.abs(signedArea(r)) > AREA_EPS)
  const sign = Math.sign(signedArea(ring)) || 1
  const holes = res.filter((r) => Math.sign(signedArea(r)) !== sign)
  const outers = res.filter((r) => Math.sign(signedArea(r)) === sign)
  if (!holes.length) return outers
  if (depth > 6) return outers   // degenerate — drop the hole rather than loop
  const hb = boxOf(holes[0])
  const xm = (hb[0] + hb[2]) / 2
  const rb = boxOf(ring)
  const pad = 10
  const left: Pt2[] = [[rb[0] - pad, rb[1] - pad], [xm, rb[1] - pad], [xm, rb[3] + pad], [rb[0] - pad, rb[3] + pad]]
  const right: Pt2[] = [[xm, rb[1] - pad], [rb[2] + pad, rb[1] - pad], [rb[2] + pad, rb[3] + pad], [xm, rb[3] + pad]]
  const out: Pt2[][] = []
  for (const half of [left, right]) {
    for (const piece of intersectClosed([ring], [half])) {
      if (piece.length < 3 || Math.abs(signedArea(piece)) <= AREA_EPS) continue
      const oriented = Math.sign(signedArea(piece)) === sign ? piece : [...piece].reverse()
      out.push(...cutRingSimple(oriented, cuts, depth + 1))
    }
  }
  return out
}

/**
 * The solved faces with the cuts removed. Untouched faces keep their ORIGINAL object (the
 * shared-edge census in generate3d stays exact on them); clipped rings are welded back
 * onto the original vertices and onto each other, so a ridge or hip shared with a
 * neighbor still pairs edge for edge after the clipper round-trip. Faces wholly inside a
 * cut disappear.
 */
export function clipFacesToCuts<F extends RoofFace>(faces: F[], cuts: Pt2[][]): F[] {
  if (!cuts.length || !clipperReady()) return faces
  const cutBoxes = cuts.map(boxOf)
  const pool: Pt2[] = []
  for (const f of faces) for (const p of f.ring) pool.push(p)
  const weld = (p: Pt2): Pt2 => {
    for (const q of pool) if (Math.abs(q[0] - p[0]) <= WELD && Math.abs(q[1] - p[1]) <= WELD) return q
    pool.push(p)
    return p
  }
  const out: F[] = []
  for (const f of faces) {
    const fb = boxOf(f.ring)
    const near = cuts.filter((_, i) => boxesMeet(fb, cutBoxes[i]))
    if (!near.length) { out.push(f); continue }
    let pieces: Pt2[][]
    try { pieces = cutRingSimple(f.ring, near) } catch { out.push(f); continue }
    const before = Math.abs(signedArea(f.ring))
    const after = pieces.reduce((s, r) => s + Math.abs(signedArea(r)), 0)
    if (Math.abs(before - after) <= AREA_EPS * 100) { out.push(f); continue }
    for (const r of pieces) {
      const welded: Pt2[] = []
      for (const p of r) {
        const q = weld(p)
        const last = welded[welded.length - 1]
        if (last && last === q) continue
        welded.push(q)
      }
      if (welded.length > 1 && welded[0] === welded[welded.length - 1]) welded.pop()
      if (welded.length >= 3 && Math.abs(signedArea(welded)) > AREA_EPS) out.push({ ...f, ring: welded })
    }
  }
  return out
}

/** plan outline rings minus the cuts (holes stay holes — outlines are drawn, not earcut) */
export function cutOutlines(rings: Pt2[][], cuts: Pt2[][]): Pt2[][] {
  if (!cuts.length || !rings.length || !clipperReady()) return rings
  try {
    const boxes = cuts.map(boxOf)
    if (!rings.some((r) => { const b = boxOf(r); return boxes.some((c) => boxesMeet(b, c)) })) return rings
    const out = differenceClosed(rings, cuts).filter((r) => r.length >= 3 && Math.abs(signedArea(r)) > AREA_EPS)
    const area = (rs: Pt2[][]) => rs.reduce((s, r) => s + signedArea(r), 0)
    return Math.abs(area(out) - area(rings)) <= AREA_EPS * 100 ? rings : out
  } catch { return rings }
}

// ---------------------------------------------------------------------------
// the generic vertical boolean on a finished mesh
// ---------------------------------------------------------------------------

/** a polygon fragment of one source triangle: per-vertex [x,y,z, nx,ny,nz, (u,v)] */
type Frag = number[][]

/** split a convex fragment by the vertical plane through plan line a→b */
function splitFrag(fr: Frag, a: Pt2, b: Pt2): Frag[] {
  const ex = b[0] - a[0], ey = b[1] - a[1]
  const len = Math.hypot(ex, ey) || 1
  const sd = (v: number[]) => (ex * (v[1] - a[1]) - ey * (v[0] - a[0])) / len
  const d = fr.map(sd)
  const EPS = 1e-7
  let pos = false, neg = false
  for (const x of d) { if (x > EPS) pos = true; else if (x < -EPS) neg = true }
  if (!pos || !neg) return [fr]
  const P: Frag = [], N: Frag = []
  for (let i = 0; i < fr.length; i++) {
    const j = (i + 1) % fr.length
    const vi = fr[i], vj = fr[j], di = d[i], dj = d[j]
    if (di >= -EPS) P.push(vi)
    if (di <= EPS) N.push(vi)
    if ((di > EPS && dj < -EPS) || (di < -EPS && dj > EPS)) {
      const t = di / (di - dj)
      const x = vi.map((c, k) => c + (vj[k] - c) * t)
      // keep the normal unit length after the lerp
      const nl = Math.hypot(x[3], x[4], x[5]) || 1
      x[3] /= nl; x[4] /= nl; x[5] /= nl
      P.push(x); N.push(x)
    }
  }
  return [P, N].filter((f) => f.length >= 3)
}

const fragCentroid = (fr: Frag): Pt2 => {
  let x = 0, y = 0
  for (const v of fr) { x += v[0]; y += v[1] }
  return [x / fr.length, y / fr.length]
}

type Mesh = NonNullable<ElementSolid['mesh']>

/** a triangle mesh with every part standing inside a cut removed; null = untouched */
function clipMesh(mesh: Mesh, cuts: Pt2[][], cutBoxes: Box[], uvs?: Float32Array): { mesh: Mesh; changed: boolean } {
  const { positions: P, normals: Nn, indices: I } = mesh
  const hasUv = !!uvs && uvs.length === (P.length / 3) * 2
  const vert = (i: number): number[] => hasUv
    ? [P[i * 3], P[i * 3 + 1], P[i * 3 + 2], Nn[i * 3], Nn[i * 3 + 1], Nn[i * 3 + 2], uvs![i * 2], uvs![i * 2 + 1]]
    : [P[i * 3], P[i * 3 + 1], P[i * 3 + 2], Nn[i * 3], Nn[i * 3 + 1], Nn[i * 3 + 2]]
  const pos: number[] = Array.from(P), nrm: number[] = Array.from(Nn), uv: number[] = hasUv ? Array.from(uvs!) : []
  const idx: number[] = []
  let vc = P.length / 3
  let changed = false
  for (let t = 0; t < I.length; t += 3) {
    const ia = I[t], ib = I[t + 1], ic = I[t + 2]
    const tb: Box = [
      Math.min(P[ia * 3], P[ib * 3], P[ic * 3]), Math.min(P[ia * 3 + 1], P[ib * 3 + 1], P[ic * 3 + 1]),
      Math.max(P[ia * 3], P[ib * 3], P[ic * 3]), Math.max(P[ia * 3 + 1], P[ib * 3 + 1], P[ic * 3 + 1]),
    ]
    const near = cuts.map((_, i) => i).filter((i) => boxesMeet(tb, cutBoxes[i]))
    if (!near.length) { idx.push(ia, ib, ic); continue }
    let frags: Frag[] = [[vert(ia), vert(ib), vert(ic)]]
    let split = false
    for (const ci of near) {
      const ring = cuts[ci]
      for (let e = 0; e < ring.length; e++) {
        const a = ring[e], b = ring[(e + 1) % ring.length]
        const eb: Box = [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[0], b[0]), Math.max(a[1], b[1])]
        const next: Frag[] = []
        for (const fr of frags) {
          if (!boxesMeet(boxOf(fr.map((v) => [v[0], v[1]] as Pt2)), eb)) { next.push(fr); continue }
          const parts = splitFrag(fr, a, b)
          if (parts.length > 1) split = true
          next.push(...parts)
        }
        frags = next
      }
      // every fragment now lies wholly inside or outside this cut
      const kept = frags.filter((fr) => !pointInRing(fragCentroid(fr), ring))
      if (kept.length !== frags.length) changed = true
      frags = kept
      if (!frags.length) break
    }
    if (!split && frags.length === 1) { idx.push(ia, ib, ic); continue }
    changed = true
    for (const fr of frags) {
      const base = vc
      for (const v of fr) {
        pos.push(v[0], v[1], v[2]); nrm.push(v[3], v[4], v[5])
        if (hasUv) uv.push(v[6], v[7])
        vc++
      }
      for (let k = 1; k + 1 < fr.length; k++) idx.push(base, base + k, base + k + 1)
    }
  }
  if (!changed) return { mesh, changed: false }
  return {
    changed: true,
    mesh: {
      ...mesh,
      positions: new Float32Array(pos),
      normals: new Float32Array(nrm),
      indices: new Uint32Array(idx),
      ...(hasUv ? { uvs: new Float32Array(uv) } : {}),
    },
  }
}

/** a flat [x0,y0,z0, x1,y1,z1, …] line list with every stretch inside a cut removed */
export function clipLineList(edges: number[], cuts: Pt2[][], cutBoxes: Box[] = cuts.map(boxOf)): number[] {
  const out: number[] = []
  for (let i = 0; i + 5 < edges.length; i += 6) {
    const A = [edges[i], edges[i + 1], edges[i + 2]], B = [edges[i + 3], edges[i + 4], edges[i + 5]]
    const sb: Box = [Math.min(A[0], B[0]), Math.min(A[1], B[1]), Math.max(A[0], B[0]), Math.max(A[1], B[1])]
    const near = cuts.filter((_, k) => boxesMeet(sb, cutBoxes[k]))
    if (!near.length) { out.push(...A, ...B); continue }
    const dx = B[0] - A[0], dy = B[1] - A[1]
    const ts = [0, 1]
    for (const ring of near) {
      for (let e = 0; e < ring.length; e++) {
        const a = ring[e], b = ring[(e + 1) % ring.length]
        const ex = b[0] - a[0], ey = b[1] - a[1]
        const den = dx * ey - dy * ex
        if (Math.abs(den) < 1e-12) continue
        const t = ((a[0] - A[0]) * ey - (a[1] - A[1]) * ex) / den
        const u = ((a[0] - A[0]) * dy - (a[1] - A[1]) * dx) / den
        if (t > 1e-9 && t < 1 - 1e-9 && u >= -1e-9 && u <= 1 + 1e-9) ts.push(t)
      }
    }
    ts.sort((p, q) => p - q)
    const at = (t: number) => [A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t]
    for (let k = 0; k + 1 < ts.length; k++) {
      const t0 = ts[k], t1 = ts[k + 1]
      if (t1 - t0 < 1e-9 && !(dx === 0 && dy === 0)) continue
      const m = at((t0 + t1) / 2)
      if (near.some((ring) => pointInRing([m[0], m[1]], ring))) continue
      out.push(...at(t0), ...at(t1))
    }
  }
  return out
}

/**
 * One finished roof-derived solid with everything inside the cuts removed — mesh
 * triangles (a true vertical boolean), feature lines, plan outlines and projected lines.
 * Returns null when nothing of it survives; the SAME object when the cuts miss it.
 */
export function clipSolidToCuts(s: ElementSolid, cuts: Pt2[][]): ElementSolid | null {
  if (!cuts.length) return s
  const cutBoxes = cuts.map(boxOf)
  let changed = false
  let mesh = s.mesh
  if (mesh) {
    const r = clipMesh(mesh, cuts, cutBoxes, mesh.uvs)
    const edges = clipLineList(mesh.edges, cuts, cutBoxes)
    if (r.changed || edges.length !== mesh.edges.length) {
      changed = true
      mesh = { ...r.mesh, edges }
    }
  }
  let paintParts = s.paintParts
  if (paintParts?.length) {
    const next = paintParts.map((pp) => {
      const r = clipMesh(pp.mesh as Mesh, cuts, cutBoxes, pp.mesh.uvs)
      const edges = clipLineList(pp.mesh.edges, cuts, cutBoxes)
      if (!r.changed && edges.length === pp.mesh.edges.length) return pp
      changed = true
      return { ...pp, mesh: { ...r.mesh, uvs: (r.mesh.uvs ?? pp.mesh.uvs) as Float32Array, edges } }
    })
    paintParts = next
  }
  let outlines = s.outlines
  if (outlines.length) {
    const next = cutOutlines(outlines, cuts)
    if (next !== outlines) { changed = true; outlines = next }
  }
  let projLines = s.projLines
  if (projLines?.length) {
    const next: NonNullable<ElementSolid['projLines']> = []
    let plChanged = false
    for (const pl of projLines) {
      if (pl.pts.length !== 2) {
        if (!pl.pts.some((p) => cuts.some((c) => pointInRing(p, c)))) next.push(pl)
        else plChanged = true
        continue
      }
      const z1 = pl.z1 ?? pl.z0
      const src = [pl.pts[0][0], pl.pts[0][1], pl.z0, pl.pts[1][0], pl.pts[1][1], z1]
      const flat = clipLineList(src, cuts, cutBoxes)
      if (flat.length === 6 && flat.every((v, i) => Math.abs(v - src[i]) < 1e-12)) { next.push(pl); continue }
      plChanged = true
      for (let i = 0; i + 5 < flat.length; i += 6) {
        next.push({ ...pl, pts: [[flat[i], flat[i + 1]], [flat[i + 3], flat[i + 4]]], z0: flat[i + 2], z1: flat[i + 5] })
      }
    }
    if (plChanged) { changed = true; projLines = next }
  }
  if (!changed) return s
  if (mesh && mesh.indices.length === 0 && !outlines.length && !(projLines?.length)) return null
  return {
    ...s,
    outlines,
    ...(mesh ? { mesh } : {}),
    ...(paintParts ? { paintParts } : {}),
    ...(projLines ? { projLines } : {}),
  }
}
